import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BellIcon, 
  ChevronLeftIcon, 
  CheckIcon,
  ClockIcon
} from '@heroicons/react/outline';
import { getNotificationsForUser, markAllNotificationsAsRead, markNotificationAsRead } from '../../services/notificationService';
import useAuthStore from '../../store/authStore';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { ar } from 'date-fns/locale/ar';

interface Notification {
  id: string;
  contractId: string;
  message: string;
  read: boolean;
  createdAt: any; // Can be Timestamp or Date
}

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications when component mounts
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user?.uid) return;
      
      try {
        setLoading(true);
        const fetchedNotifications = await getNotificationsForUser(user.uid);
        setNotifications(fetchedNotifications);
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setError('حدث خطأ أثناء جلب الإشعارات');
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [user?.uid]);

  // Mark a notification as read
  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  // Mark all notifications as read
  const handleMarkAllAsRead = async () => {
    if (!user?.uid) return;
    
    try {
      await markAllNotificationsAsRead(user.uid);
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  // Format date to relative time in Arabic
  const formatDate = (date: any) => {
    try {
      const jsDate = date?.toDate ? date.toDate() : new Date(date);
      return formatDistanceToNow(jsDate, { 
        addSuffix: true,
        locale: ar 
      });
    } catch (error) {
      return '';
    }
  };

  // Navigate to contract when notification is clicked
  const handleNotificationClick = (notification: Notification) => {
    handleMarkAsRead(notification.id);
    navigate(`/contracts/${notification.contractId}`);
  };

  // Filter unread notifications
  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="p-5 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            إعادة تحميل
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen font-tajawal">
      {/* Header */}
      <div className="sticky top-0 z-10 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-700 rotate-180" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 mr-2">الإشعارات</h2>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-blue-600 text-sm font-medium flex items-center"
            >
              <CheckIcon className="h-4 w-4 ml-1" />
              تعيين الكل كمقروء
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="p-4">
        {notifications.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 text-center mt-8">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-50">
              <BellIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">لا توجد إشعارات</h3>
            <p className="mt-1 text-sm text-gray-500">سيظهر هنا أي إشعارات جديدة تتلقاها</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <div 
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`bg-white rounded-xl p-4 shadow-sm border ${
                  !notification.read ? 'border-r-4 border-blue-500' : 'border-gray-100'
                } cursor-pointer hover:bg-gray-50 transition-colors`}
              >
                <div className="flex items-start">
                  <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                    notification.read ? 'bg-gray-100' : 'bg-blue-50'
                  }`}>
                    <BellIcon className={`h-5 w-5 ${
                      notification.read ? 'text-gray-400' : 'text-blue-600'
                    }`} />
                  </div>
                  <div className="mr-3 flex-1">
                    <p className="text-sm text-gray-800">{notification.message}</p>
                    <div className="mt-1 flex items-center text-xs text-gray-500">
                      <ClockIcon className="h-3 w-3 ml-1" />
                      <span>{formatDate(notification.createdAt)}</span>
                    </div>
                  </div>
                  {!notification.read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id);
                      }}
                      className="text-blue-600 text-xs p-1 hover:bg-blue-50 rounded-full"
                      aria-label="Mark as read"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
